import { useState } from "react";
import type { CMSTableDTO, CMSTableRowData, CMSTableRowDTO, CMSTableValueDTO } from "~/features/cms/types/dto";
import { Table, TableBody, TableHead, TableRow } from "~/common/components/Table/TableElements";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import { useTranslation } from "react-i18next";
import { Button, Link } from "~/common/mui";
import {
  ColumnImage,
  HeaderCellTitleWithTooltip,
  StyledColumnTableCell,
  StyledPinIconButton,
  StyledRowTableCell,
  TableContainer,
  TableTitle,
  ValueTextWithTooltip,
} from "~/features/cms/components/cmsTable/CMSTableStyles";

interface Props extends Omit<CMSTableDTO, "type"> {
  className?: string;
}

export const CMSTable = (props: Props) => {
  const { rows, values, title } = props;

  //create Row Data
  const formattedRows: CMSTableRowData[] = rows.map((row: CMSTableRowDTO) => {
    const rowData: CMSTableRowData = { rowHeading: row.displayName };
    // Populate row values from valuesList
    values.forEach((val: CMSTableValueDTO) => {
      if (val.row.id === row.id) {
        rowData[val.column.id] = val.value;
      }
    });
    return rowData;
  });

  // Keep track of initial column order
  const initialColumns = props.columns.map((col) => col.id);
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([]);

  const togglePinColumn = (colId: string) => {
    if (props.isSimpleView) {
      return;
    }

    setPinnedColumns((prevPinned) => {
      if (prevPinned.includes(colId)) {
        // Unpin -> remove from pinned list
        return prevPinned.filter((id) => id !== colId);
      } else {
        // Pin -> add to pinned list
        return [...prevPinned, colId];
      }
    });
  };

  const sortedColumns = [
    // Pinned columns (in order they were pinned)
    ...pinnedColumns.map((colId) => props.columns.find((col) => col.id === colId)!),
    // Unpinned columns (maintaining original order)
    ...initialColumns
      .filter((colId) => !pinnedColumns.includes(colId))
      .map((colId) => props.columns.find((col) => col.id === colId)!),
  ];

  const { isSimpleView, allowToPin } = props;
  const { t } = useTranslation();

  return (
    <>
      <TableTitle>{title}</TableTitle>
      <TableContainer>
        <Table>
          <TableHead>
            {!isSimpleView && (
              <TableRow>
                <StyledColumnTableCell>{/* Empty Cell */}</StyledColumnTableCell>
                {sortedColumns.map((col, index) => (
                  <StyledColumnTableCell addBorderTop={true} adjustPadding={true}>
                    {!isSimpleView && allowToPin && (
                      <StyledPinIconButton
                        isPinned={pinnedColumns.includes(col.id)}
                        variant="default"
                        size={"small"}
                        tabIndex={-1}
                        onClick={() => togglePinColumn(col.id)}
                      >
                        {pinnedColumns.includes(col.id) ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                      </StyledPinIconButton>
                    )}
                    {col.imageUrl && <ColumnImage src={col.imageUrl} alt="" />}
                  </StyledColumnTableCell>
                ))}
              </TableRow>
            )}
            <TableRow>
              <StyledColumnTableCell>{/* Empty Cell */}</StyledColumnTableCell>
              {sortedColumns.map((col, index) => (
                <StyledColumnTableCell addBorderTop={isSimpleView} adjustPadding={!isSimpleView}>
                  <HeaderCellTitleWithTooltip tooltipContent={col.displayName} placement="bottom-start">
                    {col.displayName}
                  </HeaderCellTitleWithTooltip>
                </StyledColumnTableCell>
              ))}
            </TableRow>
            {!isSimpleView && sortedColumns.some((col) => col.seeMoreUrl) && (
              <TableRow>
                <StyledColumnTableCell>{/* Empty Cell */}</StyledColumnTableCell>
                {sortedColumns.map((col, index) =>
                  col.seeMoreUrl != undefined ? (
                    <StyledColumnTableCell>
                      <Link href={col.seeMoreUrl} target={col.openInNewTab ? "_blank" : undefined}>
                        <Button color="secondary" size="medium">
                          {t("page.sa.housing.seemore")}
                        </Button>
                      </Link>
                    </StyledColumnTableCell>
                  ) : (
                    <StyledColumnTableCell>&nbsp;</StyledColumnTableCell>
                  ),
                )}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {formattedRows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                <StyledRowTableCell isFirstRow={rowIndex === 0} isLastRow={rowIndex === formattedRows.length - 1}>
                  <ValueTextWithTooltip tooltipContent={row.rowHeading} placement="bottom-start">
                    {row.rowHeading}
                  </ValueTextWithTooltip>
                </StyledRowTableCell>
                {sortedColumns.map((col) => (
                  <StyledRowTableCell
                    key={col.id}
                    isFirstRow={rowIndex === 0}
                    isLastRow={rowIndex === formattedRows.length - 1}
                  >
                    <ValueTextWithTooltip tooltipContent={row[col.id]} placement="bottom-start">
                      {row[col.id]}
                    </ValueTextWithTooltip>
                  </StyledRowTableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
